import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import React, { useRef, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import "react-toastify/dist/ReactToastify.css";
import { Col, Input, Label, Row } from "reactstrap";
import { IBenifit } from "api/types/_jobpost";
import UploadFileBK from "components/Common/UploadFileBK";
import { uploadFiles } from "api/uploadApi";
import { toast } from "react-toastify";
import { CONFIG_OPTION_TOAST_ERROR } from "common/toast";
import UploadFileJobPost from "components/Common/UploadFileJobPost";

export interface Props {
  formik: any;
  dataBenifits: any;
  dataConditions: any;
  setLinkImage?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

const JobInformation = ({
  formik,
  dataBenifits,
  dataConditions,
  setLinkImage,
}: Props) => {
  const { t } = useTranslation();
  const [isUploadingDescriptionImage, setIsUploadingDescriptionImage] = useState(false);
  const jobDescriptionRef = useRef<HTMLTextAreaElement>(null);

  const isBenefitExist = (
    ID: number,
    type: "benefits" | "preferences" = "benefits"
  ) => {
    const currentBenefits = formik?.values?.[type];

    return currentBenefits.some(
      (b: any) => (type == "benefits" ? b?.benefitId : b?.preferenceId) === ID
    );
  };

  const toggleBenefit = (
    benefit: IBenifit,
    type: "benefits" | "preferences" = "benefits"
  ) => {
    const currentBenefits = [...formik.values?.[type]];
    const formatBenefit = {
      [type == "benefits" ? "benefitId" : "preferenceId"]: benefit.id,
      customText: benefit.name,
      isCustom: benefit.isCustom,
    };

    if (isBenefitExist(benefit.id, type)) {
      formik.setFieldValue(
        type,
        currentBenefits.filter(
          (b: any) =>
            (type == "benefits" ? b?.benefitId : b?.preferenceId) !==
            benefit?.id
        )
      );
    } else {
      formik.setFieldValue(type, [...currentBenefits, formatBenefit]);
    }
  };

  const handleOtherTextChange = (
    type: "benefits" | "preferences" = "benefits",
    benefitId: number,
    value: string
  ) => {
    const currentBenefits = [...formik?.values?.[type]];

    const formatBenefitCustom = currentBenefits?.map((b: any) => {
      if ((type == "benefits" ? b?.benefitId : b?.preferenceId) === benefitId) {
        return {
          ...b,
          customText: value,
        };
      }
      return b;
    });

    formik.setFieldValue(type, formatBenefitCustom);
  };

  const handleJobDescriptionPaste = async (
    event: React.ClipboardEvent<HTMLTextAreaElement>
  ) => {
    if (isUploadingDescriptionImage) {
      return;
    }

    const clipboardItems = event.clipboardData?.items;
    if (!clipboardItems) {
      return;
    }

    const imageFiles: File[] = [];
    for (let i = 0; i < clipboardItems.length; i++) {
      const item = clipboardItems[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file && file.type.startsWith("image/")) {
          imageFiles.push(file);
        }
      }
    }

    if (!imageFiles.length) {
      return;
    }

    event.preventDefault();

    const textarea = event.currentTarget;
    const selectionStart = textarea.selectionStart ?? 0;
    const selectionEnd = textarea.selectionEnd ?? 0;
    const currentValue = formik?.values?.jobDescription || "";
    const clipboardText = event.clipboardData.getData("text/plain") || "";

    let uploadedMarkup = "";

    setIsUploadingDescriptionImage(true);
    try {
      const formData = new FormData();
      imageFiles.forEach((file) => {
        formData.append("files", file);
      });

      const resUpload = await uploadFiles(formData);
      if (resUpload?.data?.length) {
        uploadedMarkup = resUpload.data
          .map(
            (item: any) =>
              `<p><img src="${item?.url}" alt="" style="max-width: 100%; height: auto;" /></p>`
          )
          .join("\n");
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || t("File type not supported");
      toast(`${errorMessage}`, CONFIG_OPTION_TOAST_ERROR);
    } finally {
      setIsUploadingDescriptionImage(false);
    }

    const insertSegments: string[] = [];
    if (clipboardText) {
      insertSegments.push(clipboardText);
    }
    if (uploadedMarkup) {
      insertSegments.push(uploadedMarkup);
    }

    const insertContent = insertSegments.join("\n");
    if (!insertContent) {
      return;
    }

    const nextValue =
      currentValue.slice(0, selectionStart) +
      insertContent +
      currentValue.slice(selectionEnd);

    formik.setFieldValue("jobDescription", nextValue);
    formik.setFieldTouched("jobDescription", true, false);

    const nextCaretPosition = selectionStart + insertContent.length;
    requestAnimationFrame(() => {
      jobDescriptionRef.current?.focus();
      jobDescriptionRef.current?.setSelectionRange(
        nextCaretPosition,
        nextCaretPosition
      );
    });
  };

  
  return (
    <React.Fragment>
      <div
        className="d-flex flex-column mb-4 pt-3 pb-1 px-3 position-relative"
        style={{
          border: "1px dashed var(--vz-primary)",
          borderRadius: "4px",
        }}
      >
        <span
          className="text-primary position-absolute fs-13 px-1"
          style={{
            backgroundColor: "var(--vz-secondary-bg)",
            top: "0px",
            transform: "translateY(-50%)",
          }}
        >
          {t("Job Information")}
        </span>
        <Row>
          <Col lg={12}>
            <div className="mb-3">
              <Label htmlFor="jobDescription" className="form-label">
                {t("Job description")}
                <span className="text-danger"> *</span>
              </Label>

              <div className="d-flex mb-2 gap-2">
                
                
                <textarea
                  id="jobDescription"
                  className="form-control"
                  name={`description`}
                  value={formik?.values?.jobDescription}
                  placeholder={`${t(
                    "Please write a notice to be displayed on the job posting."
                  )}`}
                  onChange={(event) => {
                    formik.setFieldValue(
                      `jobDescription`,
                      event?.target?.value
                    );
                  }}
                  onPaste={handleJobDescriptionPaste}
                  ref={jobDescriptionRef}
                  style={{ height: 80 }}
                />
              </div>

              {formik.touched.jobDescription && formik.errors.jobDescription ? (
                <div className="text-danger mt-2">
                  {formik.errors.jobDescription}
                </div>
              ) : null}
            </div>
          </Col>

          <Col xxl={12}>
            <div className="mb-3">
              <label className="form-label">
                {t("Attachment")}
                <br />
                <small>
                  {t(
                    "Upload up to 100MB in jpg, png image file format. Maximize 5 file"
                  )}
                </small>
              </label>

              <UploadFileJobPost
                formik={formik}
                fieldValue="jobAttachment"
                isMultiple={true}
                isUploadMore={
                  (formik?.values?.jobAttachment &&
                  (formik?.values?.jobAttachment as any)?.length >= 5
                    ? false
                    : true) || !formik?.values?.jobAttachment
                }
                setLinkImage={setLinkImage}
              />
              {/* <UploadFile
                    data={formik.getFieldProps("jobAttachment")?.value}
                    //setLinkImage={setLinkImage}
                    formik={formik}
                    isMultiple={true}
                    formikNameImage="jobAttachment"
                  /> */}

              {formik.errors.jobAttachment ? (
                <div className="text-danger mt-2">
                  {formik.errors.jobAttachment}
                </div>
              ) : null}
            </div>
          </Col>

          <Col lg={12}>
            <div className="mb-3">
              <Label htmlFor="postal_code" className="form-label">
                {t("Benifits")}
              </Label>
              <div
                className="d-grid gap-2"
                style={{ gridTemplateColumns: "repeat(2, 1fr)" }}
              >
                {dataBenifits.map((benefit: IBenifit) => (
                  <div
                    key={benefit.id}
                    className="d-flex gap-2 align-items-center mb-2 form-check"
                  >
                    <Input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id={`benifit-${benefit.id}`}
                      name={`benifit-${benefit.id}`}
                      checked={isBenefitExist(benefit.id)}
                      onChange={() => toggleBenefit(benefit)}
                    />

                    <Label className="mb-0" htmlFor={`benifit-${benefit.id}`}>
                      {benefit.name}
                    </Label>
                    {/* Show text input if "Other" is selected */}
                    {benefit.isCustom === true &&
                      isBenefitExist(benefit.id) && (
                        <input
                          type="text"
                          // value={(formik?.values?.benefits?.find((item: any) => item?.benefitId === benefit.id) as any)?.customText || ""}
                          value={
                            (
                              formik?.values?.benefits?.find(
                                (item: any) => item?.benefitId === benefit.id
                              ) as any
                            )?.customText === "기타"
                              ? ""
                              : (
                                  formik?.values?.benefits?.find(
                                    (item: any) =>
                                      item?.benefitId === benefit.id
                                  ) as any
                                )?.customText
                          }
                          onChange={(event) => {
                            handleOtherTextChange(
                              "benefits",
                              benefit.id,
                              event?.target?.value
                            );
                          }}
                          placeholder="Custom benefit"
                          className="ms-3 form-control"
                          style={{ maxWidth: "200px" }}
                        />
                      )}
                  </div>
                ))}
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className="mb-3">
              <Label htmlFor="postal_code" className="form-label">
                {t("Preferred Conditions")}
              </Label>
              <div
                className="d-grid gap-2"
                style={{ gridTemplateColumns: "repeat(2, 1fr)" }}
              >
                {dataConditions.map((condition: IBenifit) => (
                  <div
                    key={condition.id}
                    className="d-flex gap-2 align-items-center mb-2 form-check"
                  >
                    <Input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id={`condition-${condition.id}`}
                      name={`condition-${condition.id}`}
                      checked={isBenefitExist(condition.id, "preferences")}
                      onChange={() => toggleBenefit(condition, "preferences")}
                    />

                    <Label
                      className="mb-0"
                      htmlFor={`condition-${condition.id}`}
                    >
                      {condition.name}
                    </Label>
                    {/* Show text input if "Other" is selected */}
                    {condition.isCustom === true &&
                      isBenefitExist(condition.id, "preferences") && (
                        <input
                          type="text"
                          // value={(formik?.values?.preferences?.find((item: any) => item?.preferenceId === condition.id) as any)?.customText || ""}
                          value={
                            (
                              formik?.values?.preferences?.find(
                                (item: any) =>
                                  item?.preferenceId === condition.id
                              ) as any
                            )?.customText === "기타"
                              ? ""
                              : (
                                  formik?.values?.preferences?.find(
                                    (item: any) =>
                                      item?.preferenceId === condition.id
                                  ) as any
                                )?.customText
                          }
                          onChange={(event) => {
                            handleOtherTextChange(
                              "preferences",
                              condition.id,
                              event?.target?.value
                            );
                          }}
                          placeholder="Custom condition"
                          className="ms-3 form-control"
                          style={{ maxWidth: "200px" }}
                        />
                      )}
                  </div>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </React.Fragment>
  );
};

export default JobInformation;
